import { useEffect, useRef, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { Box, Button, Flex, Mentions, Stack } from "@/storybook-components";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { editComment, postComment } from "@/redux/features/brainstormers/thunk";
import { setStatus } from "@/redux/features/brainstormers/slice";

import type { IdeaComments } from "@/types";

const footerButtons = (
  cancelHandler,
  cancelButtonText,
  isCancelDisabled,
  submitHandler,
  submitButtonText,
  isSubmitDisabled,
  isSubmitLoading
) => {
  return (
    <Flex
      py={6}
      alignSelf="stretch"
      justifyContent={{ base: "space-between", lg: "flex-end" }}
      w="full"
      gap={6}
    >
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        variant="secondary"
        onClick={cancelHandler}
        isDisabled={isCancelDisabled}
      >
        {cancelButtonText}
      </Button>
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        variant="primary"
        isLoading={isSubmitLoading}
        onClick={submitHandler}
        isDisabled={isSubmitDisabled}
      >
        {submitButtonText}
      </Button>
    </Flex>
  );
};
const ViewComment = ({ comment, spam }: { comment: string; spam: boolean }) => {
  const { translate } = useTranslator();
  const spamNote = translate("brainstormers.commentHidden");
  return (
    <Mentions
      value={spam ? spamNote : comment}
      showText={true}
      trigger={"@"}
      data={[]}
    />
  );
};

const CreateComment = ({
  mentions,
  setShowCommentBox
}: {
  mentions: Array<any>;
  setShowCommentBox: (show: boolean) => void;
}) => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();

  const [comment, setComment] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);

  const { status } = useAppSelector((state) => state?.brainstormers);
  const createCommentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (createCommentRef?.current) {
      setTimeout(() => createCommentRef?.current?.focus(), 1000);
    }
  }, []);

  const suggestions = mentions
    ?.filter((user) => !selectedUsers.includes(user?.login))
    ?.map((user) => ({ id: user.id, display: user.login }));

  const handleChange = (event, newValue, textValue, users) => {
    setComment(newValue);
    setSelectedUsers(users?.map((user) => user?.id));
  };

  const cancel = () => {
    setComment("");
    setShowCommentBox(false);
  };

  const saveComment = async (comment, mentionedUsers) => {
    await dispatch(postComment({ comment, mentions: mentionedUsers }));
  };

  const save = async () => {
    const mentionedUsers = mentions
      .filter((mention) => selectedUsers.includes(mention.login))
      .map(({ login, id, type }) => ({
        mentioned_name: login,
        user_id: id,
        user_type: type
      }));

    saveComment(comment, mentionedUsers);
  };

  return (
    <Box w="100%" id="ehq-ideas-comment-box" display="inline">
      <Stack spacing={2} w="full">
        <Box
          as="label"
          fontSize="sm"
          fontWeight="semibold"
          lineHeight="161%"
          color="gray.800"
        >
          {translate("brainstormers.addYourText")}
        </Box>
        <Mentions
          value={comment}
          onChange={handleChange}
          inputRef={createCommentRef}
          trigger={"@"}
          data={suggestions}
        />
      </Stack>
      <Stack spacing={6}>
        {footerButtons(
          cancel,
          translate("common.cancel"),
          status === "commentSaving",
          save,
          translate("common.post"),
          !comment || !comment?.trim(),
          status === "commentSaving"
        )}
      </Stack>
    </Box>
  );
};

const EditComment = ({
  value,
  mentions,
  setEditComment
}: {
  value: IdeaComments;
  mentions: Array<any>;
  setEditComment: (comment: IdeaComments) => void;
}) => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();

  const [comment, setComment] = useState<string>(value?.comment || "");
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);

  const { status, comments } = useAppSelector((state) => state?.brainstormers);
  const editCommentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editCommentRef?.current) {
      setTimeout(() => {
        editCommentRef?.current?.focus();
        editCommentRef?.current?.setSelectionRange(
          comment.length,
          comment.length
        );
      }, 700);
    }
  }, []);

  useEffect(() => {
    if (status === "editCommentSaved") {
      setEditComment(undefined);
      dispatch(setStatus("idle"));
    }
  }, [status]);

  useEffect(() => {
    if (value?.comment) {
      setComment(value?.comment);
    }
  }, [comments]);

  const handleChange = (event, newValue, textValue, users) => {
    setComment(newValue);
    setSelectedUsers(users?.map((user) => user?.id));
  };

  const suggestions = mentions
    ?.filter((user) => !selectedUsers.includes(user?.login))
    ?.map((user) => ({ id: user.id, display: user.login }));

  const cancel = () => {
    setEditComment(undefined);
    setComment(value?.comment);
  };
  const saveEditComment = async (commentId, comment, mentionedUsers) => {
    await dispatch(
      editComment({
        commentId: value?.id,
        comment,
        mentions: mentionedUsers
      })
    );
  };
  const save = async () => {
    const mentionedUsers = mentions
      .filter((mention) => selectedUsers.includes(mention.login))
      .map(({ login, id, type }) => ({
        mentioned_name: login,
        user_id: id,
        user_type: type
      }));

    saveEditComment(value?.id, comment, mentionedUsers);
  };

  return (
    <Box w="100%" id="idea-comment-edit-box" display="inline" fontSize="16px">
      <Mentions
        value={comment}
        inputRef={editCommentRef}
        onChange={handleChange}
        trigger={"@"}
        data={suggestions}
      ></Mentions>
      {footerButtons(
        cancel,
        translate("common.cancel"),
        status === "commentSaving",
        save,
        translate("common.update"),
        value?.comment === comment || !comment || !comment?.trim(),
        status === "commentSaving"
      )}
    </Box>
  );
};

export { ViewComment, EditComment, CreateComment };
